import { useCreateSurveyMutation } from '@apps/materiality-tracker/api/materiality-assessment';
import { AssessmentConfigurationForm } from '@apps/materiality-tracker/components/assessment-create/AssessmentConfigurationForm';
import { useCreateAssessmentForm } from '@apps/materiality-tracker/hooks/useCreateAssessmentForm';
import { BaseStepCompProps } from '@features/onboarding/types';
import { generateErrorToast } from '@g17eco/molecules/toasts';
import { Loader } from '@g17eco/atoms/loader';
import { useAppSelector } from '@reducers/index';
import { getCurrentUser } from '@selectors/user';
import { getEffectiveDate } from '@utils/survey';
import { useEffect } from 'react';
import { Button } from 'reactstrap';

export const CreateAssessmentForm = ({
  handleNext,
  handlePrevious,
}: Pick<BaseStepCompProps, 'handlePrevious' | 'handleNext'>) => {
  const currentUser = useAppSelector(getCurrentUser);
  const initiativeId = currentUser?.permissions.at(-1)?.initiativeId;
  const { assessmentForm, updateAssessmentForm, utrs, isAllUtrsAnswered, handleOptionChange } = useCreateAssessmentForm(
    { initiativeId },
  );

  const { month, year, assessmentType, answers, verificationRequired } = assessmentForm;
  const isFormValid = month && year && isAllUtrsAnswered && assessmentType;

  const [createSurvey, { isLoading: isCreating }] = useCreateSurveyMutation();

  useEffect(() => {
    if (currentUser && !initiativeId) {
      handlePrevious?.();
    }
  }, [currentUser, handleNext, handlePrevious, initiativeId]);

  const handleCreate = () => {
    if (!isFormValid || !initiativeId) {
      return;
    }
    createSurvey({
      initiativeId,
      effectiveDate: getEffectiveDate(month, year),
      answers,
      assessmentType,
      verificationRequired,
    })
      .unwrap()
      .then(() => {
        handleNext();
      })
      .catch((e) => {
        generateErrorToast(e);
      });
  };

  if (isCreating) {
    return <Loader />;
  }

  return (
    <div className='d-flex gap-5'>
      <div>
        <h4 className='fw-bold'>Assessment details</h4>
        <div className='text-ThemeHeadingLight mt-3'>
          <p>
            Please share some essential information about your company. By providing these details, we can better
            understand your needs and recommend the most suitable materiality assessment for you.
          </p>
          <p>
            After submitting your information, you'll be directed to a landing page where a member of our friendly sales
            team will reach out to you. They'll assist you in accessing MT and guide you through the payment process.
          </p>
        </div>
      </div>
      <div>
        <AssessmentConfigurationForm
          verificationRequired={verificationRequired}
          assessmentType={assessmentType}
          updateAssessmentForm={updateAssessmentForm}
          handleOptionChange={handleOptionChange}
          utrs={utrs}
        />
        <div className='mt-5 d-flex'>
          {handlePrevious ? (
            <Button color='link-secondary' onClick={handlePrevious}>
              <i className='far fa-angle-left mr-2' />
              Back
            </Button>
          ) : null}
          <Button color='primary' className='ml-auto' onClick={handleCreate} disabled={!isFormValid}>
            Next
            <i className='far fa-angle-right ml-2' />
          </Button>
        </div>
      </div>
    </div>
  );
};
